import React from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";

import {
  trackEventKeepRenewalFeedback,
  trackEventTurnRenewalOffFeedback,
  trackEventSendFeedback,
  trackEventCloseFeedbackModal,
} from "utils/gAnalyticsUtils";
import SpinningCloseButton from "components/SpinningCloseButton";
import { appRoutes, externalRoutes } from "routes";
import { useCancellationFeedback, useRouter } from "hooks";

import useStyles from "./useStyles";

const CancelFeedbackModal = (props) => {
  const { onClose, onFeedbackSent } = props;
  const classes = useStyles();
  const { push } = useRouter();
  const { createCancellationFeedback } = useCancellationFeedback();

  const handleCloseModal = () => {
    trackEventCloseFeedbackModal();
    onClose();
  };

  const handleKeepOn = () => {
    trackEventKeepRenewalFeedback();
    onClose();
    push(appRoutes.subscriptionDetailsPath());
  };

  const formik = useFormik({
    initialValues: {
      value: "price",
      otherReason: "",
    },
    onSubmit: (values) => {
      const params = {
        data: {
          type: "cancellation-feedbacks",
          attributes: {
            "cancellation-reason": values.value,
            other: values.otherReason,
          },
        },
      };

      createCancellationFeedback(params);
      trackEventSendFeedback(values.value);
      trackEventTurnRenewalOffFeedback();
      onClose();
      onFeedbackSent();
    },
  });
  const { getFieldProps, handleSubmit, setFieldValue } = formik;

  const handleChange = (event) => {
    const selection = event.target.value;

    if (selection !== "other") {
      setFieldValue("otherReason", "");
    }

    setFieldValue("value", event.target.value);
  };

  const handleOtherFocus = () => {
    setFieldValue("value", "other");
  };

  return (
    <Dialog
      open
      scroll="body"
      onBackdropClick={() => handleCloseModal()}
      classes={{ root: classes.root }}
      PaperProps={{ classes: { root: classes.paper } }}
    >
      <SpinningCloseButton onClick={handleCloseModal} />

      <DialogTitle disableTypography classes={{ root: classes.title }}>
        Membership Renewal
      </DialogTitle>

      <Typography>Please help us learn more about why you decided to cancel your account.</Typography>

      <Typography className={classes.italicText}>
        Your responses will be kept confidential and will not be shared with any other party.
      </Typography>

      <Typography className={classes.boldText}>
        Why are you turning off your renewal plan? <span>*</span>
      </Typography>

      <form onSubmit={handleSubmit}>
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="reason"
            name="reason"
            {...getFieldProps("value")}
            onChange={handleChange}
            classes={{ root: classes.radioButton }}
          >
            <FormControlLabel value="price" control={<Radio />} label="Price." />
            <FormControlLabel value="music" control={<Radio />} label="Not enough music selection." />
            <FormControlLabel value="not_funny" control={<Radio />} label="It’s not funny." />
            <FormControlLabel value="usage" control={<Radio />} label="I don’t use it anymore." />
            <FormControlLabel value="ux" control={<Radio />} label="I don’t know how to use it." />
            <FormControlLabel
              value="other"
              control={<Radio />}
              label={
                <div className={classes.row}>
                  <Typography>Other</Typography>
                  <TextField variant="standard" onFocus={handleOtherFocus} {...getFieldProps("otherReason")} />
                </div>
              }
            />
          </RadioGroup>
        </FormControl>

        <Button type="submit" color="primary" className={classes.action}>
          Send Feedback and Continue
        </Button>
      </form>

      <Button variant="contained" color="secondary" onClick={handleKeepOn}>
        Keep Renewal On
      </Button>

      <Typography variant="caption" display="block" className={classes.captionText}>
        If you have any questions regarding this membership, please{" "}
        <a href={externalRoutes.helpPath()} rel="noreferrer" target="_blank">
          visit our Support Center
        </a>{" "}
        for Help.
      </Typography>
    </Dialog>
  );
};

CancelFeedbackModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onFeedbackSent: PropTypes.func.isRequired,
};

export default CancelFeedbackModal;
